/**
 * wagmi + RainbowKit configuration.
 *
 * RainbowKit owns wallet discovery, the connect dialog and the mobile deep
 * links now, replacing the hand-rolled EIP-6963 picker this portal used to
 * ship. On a phone the portal runs in Safari or Chrome, where nothing is
 * injected at all. RainbowKit routes most wallets through WalletConnect and
 * routes MetaMask through MetaMask SDK; both transports keep signing inside
 * the selected wallet app.
 *
 * The wallet list is explicit rather than "everything RainbowKit knows". On a
 * phone WalletConnect leads into its verified app chooser; desktop keeps the
 * familiar direct MetaMask and Trust Wallet choices. The remaining supported
 * wallets stay in one bounded courtesy group instead of an unreviewed catalog.
 *
 * Nothing in this module reads, requests or stores a seed phrase or private
 * key. Signing happens entirely inside the user's wallet.
 */
import { connectorsForWallets } from "@rainbow-me/rainbowkit";
import type { Wallet, WalletList } from "@rainbow-me/rainbowkit";
import {
  binanceWallet,
  bitgetWallet,
  injectedWallet,
  metaMaskWallet,
  okxWallet,
  safepalWallet,
  tokenPocketWallet,
  trustWallet,
  walletConnectWallet,
} from "@rainbow-me/rainbowkit/wallets";
import { createConfig } from "wagmi";
import { fallback, http } from "viem";
import { bsc, type Chain } from "wagmi/chains";

import { browserTransport, chain } from "./chain.client";
import { BSC_TESTNET_CHAIN_ID } from "./config";
import { BSC_MAINNET_RPC_URLS } from "./rpc-endpoints";
import {
  isMobileWalletBrowser,
  isTrustWalletBrowser,
  withWalletConnectBootstrapChain,
} from "./wallet-connect";
import { runtimeConfig } from "./runtime-config";

/**
 * `chain` is a union of the three deployments this build could target, resolved
 * by env at runtime. Widened to `Chain` so wagmi types the config against one
 * chain rather than demanding a transport for all three.
 */
const activeChain: Chain = chain;

const APP_NAME = "AsetQu";

/**
 * WalletConnect Cloud project id.
 *
 * Required by `connectorsForWallets` whenever the catalog contains a remote
 * wallet. MetaMask mobile uses its own SDK in this RainbowKit release, but the
 * catalog still cannot be constructed without this id. The throw would happen
 * at module scope and take the whole portal down, so an unset id degrades to
 * injected-only instead: extensions keep working, mobile wallet apps do not.
 */
const projectId = runtimeConfig().walletConnectProjectId.trim();

/**
 * `injectedWallet`, but honest about whether there is anything to connect to.
 *
 * RainbowKit greys out a wallet whose `installed` is `false`, and shows one
 * that omits the field as ready. The stock `injectedWallet` omits it — so on a
 * phone, where Safari and Chrome inject nothing, "Browser Wallet" renders as a
 * live button that opens no app, resolves no deep link, and reports no error.
 * A dead button is where mobile users give up, and it would sit right beneath
 * the wallets that do work.
 *
 * Kept rather than dropped because it is the last resort for an in-app browser
 * that injects a provider but announces no EIP-6963 identity and is none of the
 * wallets named above. Greyed out, it costs nothing; enabled, it misleads.
 */
function detectedInjectedWallet(): Wallet {
  return {
    ...injectedWallet(),
    installed:
      typeof window !== "undefined" &&
      typeof (window as { ethereum?: unknown }).ethereum !== "undefined",
  };
}

type WalletFactory = WalletList[number]["wallets"][number];

/** Public BSC endpoints used only during Trust Wallet's testnet handshake. */
const walletConnectBootstrapTransport = fallback(
  BSC_MAINNET_RPC_URLS.map((url) => http(url, { timeout: 8_000, retryCount: 0 })),
  { rank: false, retryCount: 1 },
);

/** Keep direct vendor connectors on desktop, but route phones through WalletConnect. */
function desktopOnlyWallet(createWallet: WalletFactory): WalletFactory {
  return (options) => {
    const wallet = createWallet(options);
    const hiddenByWallet = wallet.hidden;

    return {
      ...wallet,
      hidden: () => isMobileWalletBrowser() || hiddenByWallet?.() === true,
    };
  };
}

/** Trust's injected connector is the preferred route inside its own DApp Browser. */
function desktopOrTrustBrowserWallet(createWallet: WalletFactory): WalletFactory {
  return (options) => {
    const wallet = createWallet(options);
    const hiddenByWallet = wallet.hidden;

    return {
      ...wallet,
      hidden: () =>
        (isMobileWalletBrowser() && !isTrustWalletBrowser()) || hiddenByWallet?.() === true,
    };
  };
}

/** A WalletConnect tile inside Trust's DApp Browser would send users into a loop. */
function outsideTrustBrowserWallet(createWallet: WalletFactory): WalletFactory {
  return (options) => {
    const wallet = createWallet(options);
    const hiddenByWallet = wallet.hidden;

    return {
      ...wallet,
      hidden: () => isTrustWalletBrowser() || hiddenByWallet?.() === true,
    };
  };
}

/**
 * Trust Wallet rejects a WalletConnect proposal containing only chain 97.
 * Establish the session on supported chain 56, then let the portal's existing
 * wrong-network control add/switch to testnet after an account is available.
 */
const compatibleWalletConnectWallet: WalletFactory = (options) => {
  const wallet = walletConnectWallet(options);

  return activeChain.id === BSC_TESTNET_CHAIN_ID
    ? withWalletConnectBootstrapChain(wallet, {
        chain: bsc,
        transport: walletConnectBootstrapTransport,
      })
    : wallet;
};

/**
 * Desktop keeps direct MetaMask and Trust Wallet choices. On mobile those two
 * vendor tiles are hidden and WalletConnect becomes the first route, so the
 * wallet chooser owns the hand-off to whichever app the holder uses.
 *
 * Every wallet left visible on a phone resolves a mobile deep link. Rabby is
 * deliberately absent: it ships no mobile app, so RainbowKit would render it
 * permanently greyed for the majority of this portal's users.
 * Desktop loses nothing — it announces `io.rabby` over EIP-6963, and wagmi's
 * discovery lists it by name on its own.
 *
 * The generic WalletConnect entry is kept separate because RainbowKit hands it
 * to Reown AppKit's wallet chooser rather than a vendor-specific deep link.
 * AppKit creates styles at runtime; `app/layout.tsx` installs the current CSP
 * nonce before client modules execute so those trusted styles remain usable
 * without weakening `style-src` to `unsafe-inline`.
 */
export const walletGroups = [
  {
    groupName: "Direkomendasikan",
    wallets: [
      desktopOnlyWallet(metaMaskWallet),
      desktopOrTrustBrowserWallet(trustWallet),
    ],
  },
  {
    groupName: "WalletConnect",
    wallets: [outsideTrustBrowserWallet(compatibleWalletConnectWallet)],
  },
  {
    groupName: "Wallet lain",
    wallets: [
      binanceWallet,
      okxWallet,
      bitgetWallet,
      tokenPocketWallet,
      safepalWallet,
      detectedInjectedWallet,
    ],
  },
] satisfies WalletList;

/** Without a project id only the injected connector is safe to construct in development. */
const injectedOnly = [
  {
    groupName: "Terpasang di browser ini",
    wallets: [detectedInjectedWallet],
  },
];

if (!projectId) {
  const message =
    "walletConnectProjectId is unset — mobile wallet apps " +
    "and the WalletConnect option cannot be offered.";

  // `import.meta.env.PROD`, not `process.env.NODE_ENV`: there is no `process`
  // in a browser, and Vite does not shim one. Referencing it throws at module
  // scope, which takes the whole portal down before anything renders — a blank
  // page whose only symptom is one line in the console.
  if (import.meta.env.PROD) throw new Error(message);
  console.warn(`${message} Only browser-injected wallets are enabled in development.`);
}

/**
 * What the wallet shows on its approval sheet.
 *
 * This is the screen a user lands on after the deep link fires, and it is the
 * one place they get to check they are approving the real portal — so leaving
 * it half-filled is a security cost, not a cosmetic one. RainbowKit defaults
 * the icon list to empty and the description to the app's name, which renders
 * as a blank tile next to a bare word: indistinguishable from a phishing clone,
 * on a portal whose own banner tells people to watch for exactly that.
 *
 * The origin is read at runtime rather than configured, so a testnet rehearsal,
 * a preview deployment and mainnet each announce themselves truthfully. On the
 * server it is left undefined and RainbowKit's own fallback applies; nothing
 * connects from there.
 */
const origin = typeof window !== "undefined" ? window.location.origin : undefined;

const connectors = connectorsForWallets(projectId ? walletGroups : injectedOnly, {
  appName: APP_NAME,
  appDescription:
    "Portal resmi migrasi token AsetQu di BNB Smart Chain. Klaim 1:1 " +
    "berdasarkan snapshot, tanpa approve token lama.",
  appUrl: origin,
  appIcon: origin ? `${origin}/asetqu.png` : undefined,
  projectId,
});

/**
 * One public chain, deliberately. The portal signs against a single deployment,
 * and a multi-chain config would let the UI treat a network with no contracts
 * as supported. The Trust Wallet bootstrap chain is injected privately into
 * its WalletConnect connector and therefore never enters wagmi's public list.
 */
export const wagmiConfig = createConfig({
  chains: [activeChain],
  connectors,
  transports: { [activeChain.id]: browserTransport },
  // Next renders these components on the server first; without this wagmi
  // reads its persisted connection during hydration and the first paint
  // disagrees with the markup.
  ssr: true,
});
